import Button from "@/common/components/Button"
import SelectController from "@/common/components/SelectController"
import Skeleton from "@/common/components/Skeleton";
import { Mapper } from "@/common/services/mapper"
import { contactStateStrategy, translateContactStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant"
import useCreatePrivateCompanyLeadContact from "@/features/privateCompanyLeadsFunnel/hooks/useCreatePrivateCompanyLeadContact"
import { PrivateCompanyLeadsFunnelUpdateValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema"
import { ContactData, ContactState, PrivateCompanyLeadData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { useIsFetching } from "@tanstack/react-query";
import { UserIcon } from "lucide-react"
import { Control } from "react-hook-form"
import { Link } from "react-router-dom"
import { twMerge } from "tailwind-merge"

interface ContactsSectionProps {
  privateCompanyLeadId: string
  leadState: PrivateCompanyLeadData['state']
  contacts?: ContactData[]
  contact_for_master_user?: ContactData
  control: Control<PrivateCompanyLeadsFunnelUpdateValidationSchema>
  submit?: () => void
  redirectOnSuccess?: (path: string) => void
}

export const ContactsSection = ({
  privateCompanyLeadId,
  leadState,
  contacts,
  contact_for_master_user,
  control,
  submit,
  redirectOnSuccess
}: ContactsSectionProps) => {
  const isLeadFetching = useIsFetching({ queryKey: ['privateCompanyLead'] });

  const {
    mutateAsync: createPrivateCompanyLeadContact,
    isPending: createPrivateCompanyLeadContactIsPending,
    isError: createPrivateCompanyLeadContactIsError,
  } = useCreatePrivateCompanyLeadContact({
    id: privateCompanyLeadId,
    onSuccess: (response) => {
      const newContact = response?.data?.data?.contacts?.find((contact: ContactData) => contacts?.every((c) => c.id !== contact.id))

      newContact && redirectOnSuccess?.(`/private-clients/${privateCompanyLeadId}/contacts/${newContact.id}/edit`)
    }
  })

  return (
    <div className="flex flex-col items-start gap-y-4 pb-1">
      {createPrivateCompanyLeadContactIsError && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <p className="text-[18px] font-bold">Contatos</p>
      {leadState === 'qualifying' && (
        <Button
          type="button"
          variant='link'
          tooltipPlace="right"
          operationKlassToCheckPermission="Admin::Funnel::PrivateCompanyLeads::Update"
          onClick={() => createPrivateCompanyLeadContact()} disabled={createPrivateCompanyLeadContactIsPending}
          name="add_contact"
        >
          Novo contato
        </Button>
      )}
      <div className="flex gap-x-4 overflow-x-auto">
        {!isLeadFetching && contacts?.map((contact) => {
          const hasPassedEveryRequirement = contact.requirement_list_summary?.steps?.every((step) => step.passed === true)

          return (
            <Link
              to={`/private-clients/${privateCompanyLeadId}/contacts/${contact.id}/edit`}
              className={
                twMerge(
                  "flex min-w-fit gap-2 items-center bg-white-100 px-4 py-2 rounded-lg cursor-pointer hover:bg-white-200",
                  contact.state === ContactState.awaiting_client_documents && 'bg-yellow-op-2 hover:bg-yellow-hover',
                  contact.state === ContactState.in_review && 'bg-blue-700 hover:bg-primary-blue-hover',
                  (hasPassedEveryRequirement && contact.state === ContactState.in_review || !((['qualifying', 'qualifying_management_review'] as PrivateCompanyLeadData['state'][]).includes(leadState))) && 'bg-success-disabled hover:bg-success-hover'
                )
              }
              key={contact.id}
              data-testid="contact_link_button"
            >
              <div className="flex justify-center items-center size-8 bg-white-800 text-black-600 rounded-full">
                <UserIcon />
              </div>
              <div>
                <p>
                  <span className="font-bold text-sm">{contact.full_name}</span> - <span className="italic">{contact.role_in_company}</span>
                </p>
                <p className="text-xs">
                  {((['qualifying', 'qualifying_management_review'] as PrivateCompanyLeadData['state'][]).includes(leadState))
                    ? translateContactStateStrategy[contactStateStrategy[contact.state as keyof typeof contactStateStrategy]]
                    : 'Visualizar'
                  }
                </p>
              </div>
            </Link>
          )
        })}

        {!!isLeadFetching && (
          <>
            <Skeleton height={55} width={160} className="bg-white-100" />
            <Skeleton height={55} width={160} className="bg-white-100" />
          </>
        )}
      </div>
      <SelectController
        control={control}
        name="contact_for_master_user"
        label="Gestor Master Frota"
        className="w-full"
        defaultValue={(contacts?.length ? contacts.map((contact) => ({ value: contact.id, label: contact.full_name || '' })) : [])?.find((contact) => contact.value === contact_for_master_user?.id)}
        options={contacts?.length ? Mapper.mapToOptions<ContactData>({ labelFieldName: 'full_name', valueFieldName: 'id' })(contacts) : []}
        triggerOnChange={submit}
        isDisabled={leadState !== 'negotiating'}
        markAsRequired
      />
    </div>
  )
}
